/**
 * Minimal HTTP helpers for calling Edge Functions with the user's JWT.
 */
import { supabase } from './supabase'

// ============= TYPES =============

export interface AdminMetricsResponse {
  ok: true;
  data: {
    totalUsers: number;
    totalOrganizations: number;
    activeUsers: number;
    totalInterviews: number;
    totalPoints: number;
    amsSubscribers: number;
    totalActivities: number;
    avgSessionDuration: string;
  };
}

export interface AdminUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  last_active: string | null;
  account_type: "agent" | "agency" | "both" | "unknown";
  subscription_tier: "free" | "basic" | "pro" | "elite";
  plan_code: string | null;
  stripe_customer_id: string | null;
  trial_ends_at: string | null;
  has_ams_access: boolean;
  total_points: number;
  rank: {
    code: string;
    name: string;
    image?: string;
    color?: string;
    type: 'agency' | 'agent';
  } | null;
  profile_completion: number | null;
  last_login_date: string | null;
  referral_code: string | null;
  organizations: Array<{
    org_id: string;
    org_name: string;
    org_referral_code?: string;
    role: string;
    joined_at: string;
  }>;
  email_confirmed: boolean;
  active_tenant_id: string | null;
}

export interface AdminUsersResponse {
  ok: true;
  data: {
    users: AdminUser[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface AdminActivity {
  id: string;
  action: string;
  subject_type: string;
  subject_id: string;
  message: string | null;
  data: Record<string, any>;
  createdat: string;
  actor_user_id: string;
  actor_email: string;
  tenant_id: string;
  org_name: string;
}

export interface AdminActivitiesResponse {
  ok: true;
  data: {
    activities: AdminActivity[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface SystemHealthResponse {
  ok: true;
  data: {
    status: "healthy" | "degraded";
    timestamp: string;
    responseTime: number;
    database: {
      healthy: boolean;
      connectionTime: number;
      totalRecords: {
        users: number;
        activities: number;
        interviews: number;
      };
    };
    performance: {
      avgResponseTime: number;
      errorRate: number;
      benchmarks: {
        dbConnection: number;
        userQuery: number;
        activityQuery: number;
        interviewQuery: number;
        orgQuery: number;
        activeUsersProcess: number;
        statsQuery: number;
        errorCalc: number;
      };
    };
    trends: {
      signups: Array<{ date: string; count: number }>;
      activities: Array<{ date: string; count: number }>;
      interviews: Array<{ date: string; count: number }>;
      organizations: Array<{ date: string; count: number }>;
      activeUsers: Array<{ date: string; count: number }>;
    };
  };
}

export interface AdminTask {
  id: string;
  tenantid: string;
  orca_task: string;
  orca_priority: 'low' | 'medium' | 'high';
  orca_status: 'Not Started' | 'In Progress' | 'Completed';
  orca_due_date: string | null;
  orca_assigned_to: string | null;
  orca_text: string | null;
  position: number;
  created_by: string | null;
  createdat: string;
  updatedat: string;
}

export interface AdminTasksResponse {
  ok: true;
  tasks: AdminTask[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SubscriptionAnalyticsResponse {
  ok: true;
  data: {
    summary: {
      totalMRR: number;
      totalAgents: number;
      totalAgencies: number;
      amsSubscribers: number;
    };
    agencies: {
      basic: number;
      pro: number;
      elite: number;
      total: number;
    };
    agents: {
      total: number;
      withAMS: number;
      withoutAMS: number;
    };
    revenue: {
      totalMRR: number;
      fmaRevenue: number;
      amsRevenue: number;
    };
    breakdown: {
      fma: {
        basic: { count: number; revenue: number; withAMS: number; amsRevenue: number };
        pro: { count: number; revenue: number; withAMS: number; amsRevenue: number };
        elite: { count: number; revenue: number; withAMS: number; amsRevenue: number };
        total: { count: number; revenue: number };
      };
      ams: {
        total: { count: number; revenue: number };
        agents: { count: number; revenue: number };
        basicAgencies: { count: number; revenue: number };
        proAgencies: { count: number; revenue: number };
        eliteAgencies: { count: number; revenue: number };
      };
      agents: {
        total: number;
        withAMS: number;
        withoutAMS: number;
      };
    };
    trends: {
      basicAgencies: Array<{ date: string; count: number }>;
      proAgencies: Array<{ date: string; count: number }>;
      eliteAgencies: Array<{ date: string; count: number }>;
      agents: Array<{ date: string; count: number }>;
      amsAgents: Array<{ date: string; count: number }>;
      amsBasic: Array<{ date: string; count: number }>;
      amsPro: Array<{ date: string; count: number }>;
      amsElite: Array<{ date: string; count: number }>;
    };
    timestamp: string;
  };
}

export async function callFunction<T=unknown>(name: string, payload?: any): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession()
  
  if (!session?.access_token) {
    throw new Error('No active session - please log in again')
  }
  
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: payload ? JSON.stringify(payload) : undefined,
  })
  
  if (!res.ok) {
    const errorText = await res.text()
    throw new Error(`Function ${name} failed: ${res.status} - ${errorText}`)
  }
  
  return await res.json() as T
}

export async function callFunctionUnauthenticated<T=unknown>(name: string, payload?: any): Promise<T> {
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: payload ? JSON.stringify(payload) : undefined,
  })
  if (!res.ok) throw new Error(`Function ${name} failed: ${res.status}`)
  return await res.json() as T
}

/**
 * Get admin dashboard metrics (super admin only)
 */
export async function getAdminMetrics(): Promise<AdminMetricsResponse> {
  return callFunction<AdminMetricsResponse>('admin_get-dashboard-metrics', {});
}

/**
 * Get all users for admin user management (super admin only)
 */
export async function getAdminUsers(params?: {
  search?: string;
  accountType?: "all" | "agent" | "agency" | "both";
  page?: number;
  limit?: number;
}): Promise<AdminUsersResponse> {
  console.log('[http.getAdminUsers] Called with params:', params);
  const result = await callFunction<AdminUsersResponse>('admin_get-users', params || {});
  console.log('[http.getAdminUsers] Received response:', result);
  return result;
}

/**
 * Delete a user and all their owned organizations (super admin only)
 */
export async function deleteAdminUser(userId: string, userEmail?: string): Promise<{
  ok: true;
  data: {
    success: boolean;
    message: string;
    details: {
      user_id: string;
      user_email: string;
      deleted_organizations: number;
      organization_names: string[];
    };
  };
}> {
  return callFunction('admin_delete-user', { user_id: userId, user_email: userEmail });
}

/**
 * Get activity logs for admin (super admin only)
 */
export async function getAdminActivities(params?: {
  startDate?: string | null;
  endDate?: string | null;
  actionFilter?: string;
  searchEmail?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}): Promise<AdminActivitiesResponse> {
  return callFunction<AdminActivitiesResponse>('admin_get-activities', params || {});
}

/**
 * Get system health and performance metrics (super admin only)
 */
export async function getSystemHealth(): Promise<SystemHealthResponse> {
  return callFunction<SystemHealthResponse>('admin_get-system-health', {});
}

/**
 * Get subscription analytics and revenue metrics (super admin only)
 */
export async function getSubscriptionAnalytics(): Promise<SubscriptionAnalyticsResponse> {
  return callFunction<SubscriptionAnalyticsResponse>('admin_get-subscription-analytics', {});
}

/**
 * Admin Task Manager - Get all tasks (super admin only)
 */
export async function getAdminTasks(params?: {
  statusFilter?: string;
  priorityFilter?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}): Promise<AdminTasksResponse> {
  return callFunction<AdminTasksResponse>('admin_task-manager', {
    operation: 'list',
    sortBy: 'position',
    sortOrder: 'asc',
    ...params,
  });
}

/**
 * Admin Task Manager - Create new task (super admin only)
 */
export async function createAdminTask(task: {
  tenantId: string;
  orca_task: string;
  orca_priority?: 'low' | 'medium' | 'high';
  orca_status?: 'Not Started' | 'In Progress' | 'Completed';
  orca_due_date?: string | null;
  orca_assigned_to?: string | null;
  orca_text?: string | null;
}): Promise<{ ok: boolean; task: AdminTask }> {
  return callFunction('admin_task-manager', {
    operation: 'create',
    task,
  });
}

/**
 * Admin Task Manager - Update existing task (super admin only)
 */
export async function updateAdminTask(taskId: string, task: Partial<{
  orca_task: string;
  orca_priority: 'low' | 'medium' | 'high';
  orca_status: 'Not Started' | 'In Progress' | 'Completed';
  orca_due_date: string | null;
  orca_assigned_to: string | null;
  orca_text: string | null;
  position: number;
}>): Promise<{ ok: boolean; task: AdminTask }> {
  return callFunction('admin_task-manager', {
    operation: 'update',
    taskId,
    task,
  });
}

/**
 * Admin Task Manager - Delete task (super admin only)
 */
export async function deleteAdminTask(taskId: string): Promise<{ ok: boolean; message: string }> {
  return callFunction('admin_task-manager', {
    operation: 'delete',
    taskId,
  });
}

/**
 * Helper for logging activities consistently.
 */
export const activities = {
  log: async (params: {
    tenantId: string
    actor_user_id: string
    action: string
    subject_type: string
    subject_id: string
    message?: string
    data?: Record<string, any>
  }) => {
    const { error } = await supabase.from('activities').insert({
      tenantid: params.tenantId, // Note: lowercase column name
      actor_user_id: params.actor_user_id,
      action: params.action,
      subject_type: params.subject_type,
      subject_id: params.subject_id,
      message: params.message,
      data: params.data || {},
    })
    if (error) {
      // Non-blocking - silently fail
    }
  },
}